import React from 'react'
import { Link } from '@resiliatech/birdsong'
import { useQuery } from '@apollo/client'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import { CHART } from 'Apollo/Queries/Chart'
import { categoriesIncludingOld } from 'TypeDefs/Goal'
import {
  ChartQuery,
  Goal,
  GoalCategory,
  UserSurveyPriority,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  GoalChip,
  GoalChipContainer,
  GoalProgressCardContainer,
  GoalTitle,
} from './GoalProgress.styled'
import { ChartProgress } from './ChartProgress'
import { GOALS_PATH } from '../../GoalsDashboard/types'
import { calculateChartProgress } from './utils'
import { WithWidgetLoading } from '../WithWidgetLoading'
import { dispatchGoalClicked } from '../analytics.utils'

type GoalProgressCardProps = {
  goal: Goal
  userSurveyPriorities: UserSurveyPriority[]
}

export const GoalProgressCard = ({
  goal,
  userSurveyPriorities,
}: GoalProgressCardProps) => {
  const { userConfig, nonprofitOrgConfig } = useAppState()
  const { data: { chart } = {}, loading } = useQuery<ChartQuery>(CHART, {
    variables: { goalId: goal.id },
  })

  const { id: userId, email: userEmail } = userConfig
  const {
    orgId: nonprofitOrgId,
    platformPlan,
    funderOrgId,
  } = nonprofitOrgConfig

  const isPriorityCategory = (goalCategory: GoalCategory | undefined | null) =>
    userSurveyPriorities.some((priority) => priority.category === goalCategory)

  const goalCategory =
    goal.category && categoriesIncludingOld[goal.category]
      ? categoriesIncludingOld[goal.category]
      : ''

  const chartProgress = calculateChartProgress(chart)

  const isPriority = isPriorityCategory(goal.category)

  const completedTasks = goal.ChecklistItem.filter(
    (task) => task.isCompleted
  ).length

  const totalTasks = goal.ChecklistItem.length

  const taskStatusPercentage =
    totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  const taskStatusIndicator =
    taskStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <CircularProgress
        variant='determinate'
        value={taskStatusPercentage}
        size={20}
        color='inherit'
      />
    )

  return (
    <WithWidgetLoading
      loading={loading}
      showBorderAndPadding={false}
      width='auto'
      height='auto'
    >
      <GoalProgressCardContainer>
        <GoalTitle
          onClick={() => {
            dispatchGoalClicked({
              goalTitle: goal.title,
              goalId: goal.id,
              goalCategory: goal.category || '',
              funderOrgId: funderOrgId || null,
              userId,
              userEmail,
              orgId: nonprofitOrgId,
              platformPlan: platformPlan || '',
            })
          }}
        >
          <Link
            href={`${GOALS_PATH}/${goal.id}?section=tasks`}
            target='_blank'
            rel='noopener'
          >
            {goal.title}
          </Link>
        </GoalTitle>
        <GoalChipContainer>
          {totalTasks > 0 && (
            <GoalChip
              variant={
                taskStatusPercentage === 100 ? 'completed' : 'informational'
              }
            >
              {taskStatusPercentage > 0 && taskStatusIndicator}
              {completedTasks} / {totalTasks} Tasks Completed
            </GoalChip>
          )}
          {goalCategory && (
            <GoalChip variant='category'>{goalCategory}</GoalChip>
          )}
          {isPriority && (
            <GoalChip variant='priority'>Identified as Priority</GoalChip>
          )}
        </GoalChipContainer>
        {!!chartProgress && <ChartProgress chartProgress={chartProgress} />}
      </GoalProgressCardContainer>
    </WithWidgetLoading>
  )
}
